import { useAction, useAtom } from '@reatom/npm-react'
import { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { SegmentControl } from '@blade/ui-kit'
import { pairFocusEvent } from '@root/entities/pair'
import { STATS_PERIODS } from '@root/entities/pair/model/pair-metrics'
import { tableExploreFiltersAtom } from '@root/features/explore'
import { exploreTablePeriodAtom } from '@root/features/explore/atoms/explore-table'
import { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'
import { ExploreFilterButtonSmall } from '@root/features/explore/ui/explore-filter'
import {
  ExploreFilterDrawer,
  ExploreList,
} from '@root/features/explore/ui/explore-filter/mobile'
import { ExploreNewList } from '@root/features/explore/ui/explore-pairs/explore-new-list'
import { ExploreTopGainersList } from '@root/features/explore/ui/explore-pairs/explore-top-gainers-list'
import { ExploreTrendingList } from '@root/features/explore/ui/explore-pairs/explore-trending-list'
import { QuickBuyInput } from '@root/features/trade/quick-buy/ui'
import { Screen } from '@root/shared/layout'

import { exploreTabAtom } from './atoms/explore-tab-atom'
import { ExploreScreenTabs } from './ui'

import type { Pair } from '@root/entities/pair'

const TAB_TO_ROWS_MAP: Record<ExploreFeedType, 1 | 2> = {
  [ExploreFeedType.New]: 2,
  [ExploreFeedType.Trending]: 2,
  [ExploreFeedType.TopGainers]: 1,
  [ExploreFeedType.Cooking]: 2,
  [ExploreFeedType.Graduated]: 2,
}

export const ExploreScreen = () => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [exploreTab] = useAtom(exploreTabAtom)
  const [disableTableScroll, setDisableTableScroll] = useState(false)

  const [explorePeriod, setExplorePeriod] = useAtom(exploreTablePeriodAtom)
  const focusPair = useAction(pairFocusEvent)

  const navigateToPair = ({ pair }: { pair: Pair.PairMinimal }) => {
    focusPair({
      network: pair.network,
      address: pair.address,
    })
  }

  const TAB_TO_VIEW_MAP: Record<ExploreFeedType, React.ReactNode> = {
    [ExploreFeedType.New]: <ExploreNewList onItemClick={navigateToPair} />,
    [ExploreFeedType.Trending]: <ExploreTrendingList />,
    [ExploreFeedType.TopGainers]: (
      <ExploreTopGainersList
        tab={ExploreFeedType.TopGainers}
        onItemClick={navigateToPair}
        offsetBottom={'calc(76px + var(--safe-area-inset-bottom))'}
      />
    ),
    [ExploreFeedType.Cooking]: (
      <ExploreTopGainersList
        tab={ExploreFeedType.Cooking}
        onItemClick={navigateToPair}
        offsetBottom={'calc(76px + var(--safe-area-inset-bottom))'}
      />
    ),
    [ExploreFeedType.Graduated]: (
      <ExploreTopGainersList
        tab={ExploreFeedType.Cooking}
        onItemClick={navigateToPair}
        offsetBottom={'calc(76px + var(--safe-area-inset-bottom))'}
      />
    ),
  }

  // Sticky table scroll
  useLayoutEffect(() => {
    const rootEl = rootRef.current
    if (!rootEl) {
      return
    }
    rootEl.scrollTop = 0

    const handleScroll = () => {
      if (
        rootEl.offsetHeight + Math.round(rootEl.scrollTop) >=
        rootEl.scrollHeight
      ) {
        setDisableTableScroll(true)
        return
      }
      setDisableTableScroll(false)
    }

    rootEl.addEventListener('scroll', handleScroll, {
      passive: true,
    })

    return () => {
      rootEl.removeEventListener('scroll', handleScroll)
    }
  }, [exploreTab])

  const handleTableScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (!disableTableScroll) {
      return
    }

    if (event.currentTarget.scrollTop === 0) {
      setDisableTableScroll(true)
    }
  }

  return (
    <Root>
      <ExploreScreenTabs />
      <Body ref={rootRef} $rows={TAB_TO_ROWS_MAP[exploreTab]}>
        {TAB_TO_VIEW_MAP[exploreTab]}
        {exploreTab === ExploreFeedType.TopGainers ? undefined : (
          <Filter>
            <FilterControls>
              <SegmentControl
                size="h30"
                value={explorePeriod}
                onChange={setExplorePeriod}
              >
                {STATS_PERIODS.map((period) => (
                  <FilterItem key={period} value={period}>
                    {period}
                  </FilterItem>
                ))}
              </SegmentControl>
              <Side>
                <QuickBuyInput
                  section="exploreFeeds"
                  withUsd={false}
                  withSettings={false}
                  withLabel={false}
                  withPresetSelector={false}
                  withGuestHidden={false}
                  size="h32"
                />
                <ExploreFilterButtonSmall model={tableExploreFiltersAtom} />
              </Side>
            </FilterControls>
            <FilterTable>
              <ExploreList
                onScroll={handleTableScroll}
                disableScroll={!disableTableScroll}
              />
            </FilterTable>
          </Filter>
        )}
      </Body>
      <ExploreFilterDrawer />
      <Floating>
        <ExploreFilterButtonSmall
          model={tableExploreFiltersAtom}
          variant="floating"
        />
        <QuickBuyInput
          section="exploreFeeds"
          withUsd
          withSettings
          withLabel
          withPresetSelector
          withGuestHidden={false}
          size="h28"
        />
      </Floating>
    </Root>
  )
}

const Root = styled(Screen)`
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  padding-top: 0;
  background: var(--background-1);
`

const Body = styled.div<{ $rows: 1 | 2 }>`
  display: grid;
  grid-template-rows: ${(props) =>
    props.$rows === 1 ? '100%' : 'calc(var(--viewport-height) * 0.6) 100%'};
  overflow: auto;

  &::-webkit-scrollbar {
    display: none; // Safari and Chrome
  }

  mask-image: linear-gradient(
    to bottom,
    transparent,
    black 8px,
    black 100%,
    transparent
  );
  mask-size: 100%;
  mask-position: 0 0;
  mask-repeat: no-repeat, no-repeat;
`
const Filter = styled.div`
  background: var(--background-2);
  height: 100%;
  position: relative;
  display: grid;
  grid-template-rows: auto 1fr;
`
const FilterControls = styled.div`
  border-top: 1px solid var(--background-3);
  padding: 8px 12px;
  display: grid;
  grid-template-columns: auto auto;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
`

const Side = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 3px;
  align-items: center;
`

const FilterTable = styled.div``

const FilterItem = styled(SegmentControl.Item)``

const Floating = styled.div`
  position: fixed;
  bottom: calc(72px + var(--safe-bottom));
  left: 10px;
  right: 10px;

  border-radius: 10px;
  border: 1px solid var(--backgroundA-3);
  background: var(--backgroundA-2);
  backdrop-filter: blur(25px);

  display: grid;
  grid-template-columns: auto minmax(0, max-content);
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  min-width: 0;

  padding: 8px;
`
